<script>
  import { onMount } from "svelte";
  import { apiRequest } from "../utils/api";
  import { renderMarkdown } from "../utils/markdown.js";

  // URL-owned selected topic slug (mirrors selectedProjectId in CatalogManager).
  // null = index/list view (no topic deep-linked).
  export let selectedTopic = null;
  // onSelectTopic(slug): ask AppShell to select a topic + push /help/:slug.
  export let onSelectTopic = () => {};

  let topics = [];
  let loadingTopics = false;
  let topicsError = null;

  let article = null; // { slug, title, body }
  let loadingArticle = false;
  let articleError = null;
  let loadedForSlug = null; // guards against a stale response clobbering a newer selection

  onMount(() => {
    loadTopics();
  });

  async function loadTopics() {
    loadingTopics = true;
    topicsError = null;
    try {
      topics = await apiRequest("/api/help/topics");
      // Deep-link with no topic in the URL: default-select the first topic so
      // the right pane is never empty on a bare /help visit.
      if (!selectedTopic && topics.length > 0) {
        onSelectTopic(topics[0].slug);
      }
    } catch (error) {
      topicsError = error?.message || "Could not load help topics.";
      topics = [];
    } finally {
      loadingTopics = false;
    }
  }

  // Group topics by their manifest `group`, preserving manifest (server) order
  // for both groups and topics within a group.
  function groupedTopics(list) {
    const groups = [];
    const byGroup = new Map();
    for (const topic of list) {
      const key = topic.group || "";
      if (!byGroup.has(key)) {
        const bucket = { group: key, items: [] };
        byGroup.set(key, bucket);
        groups.push(bucket);
      }
      byGroup.get(key).items.push(topic);
    }
    return groups;
  }

  $: groups = groupedTopics(topics);

  async function loadArticle(slug) {
    if (!slug) {
      article = null;
      return;
    }
    loadingArticle = true;
    articleError = null;
    const requestedSlug = slug;
    try {
      const result = await apiRequest(`/api/help/topics/${encodeURIComponent(slug)}`);
      if (requestedSlug !== loadedForSlug && requestedSlug !== selectedTopic) {
        // Selection moved on again while this was in flight; drop it.
      }
      article = result;
    } catch (error) {
      articleError = error?.message || "Could not load this help article.";
      article = null;
    } finally {
      loadedForSlug = requestedSlug;
      loadingArticle = false;
    }
  }

  // Deep-link / topic switch (URL-owned) drives the article fetch.
  $: if (selectedTopic && selectedTopic !== loadedForSlug) {
    loadArticle(selectedTopic);
  }
</script>

<div class="help-shell" data-testid="help-section">
  <h2 class="text-base font-bold text-neutral-800 mb-4">Help</h2>

  <div class="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-5">
    <!-- Left rail: topic list, grouped -->
    <nav class="space-y-4" aria-label="Разделы справки">
      {#if loadingTopics}
        <p class="text-xs text-neutral-400">Загрузка…</p>
      {:else if topicsError}
        <p class="text-xs text-danger-text" role="alert">{topicsError}</p>
      {:else}
        {#each groups as bucket (bucket.group)}
          <div class="space-y-1">
            {#if bucket.group}
              <p class="text-[0.68rem] font-bold text-neutral-400 uppercase tracking-wider px-1">{bucket.group}</p>
            {/if}
            <ul class="space-y-0.5">
              {#each bucket.items as topic (topic.slug)}
                <li>
                  <button
                    type="button"
                    class="w-full text-left rounded-md px-2.5 py-1.5 text-[0.8rem] transition-colors {selectedTopic === topic.slug ? 'bg-brand-500 text-white font-semibold' : 'text-neutral-700 hover:bg-neutral-100'}"
                    data-testid="help-topic-link"
                    aria-current={selectedTopic === topic.slug ? "page" : undefined}
                    on:click={() => onSelectTopic(topic.slug)}
                  >
                    {topic.title}
                  </button>
                </li>
              {/each}
            </ul>
          </div>
        {/each}
      {/if}
    </nav>

    <!-- Right pane: the selected article -->
    <article class="min-w-0" data-testid="help-article">
      {#if loadingArticle}
        <p class="text-xs text-neutral-400">Загрузка…</p>
      {:else if articleError}
        <p class="text-xs text-danger-text" role="alert">{articleError}</p>
      {:else if article}
        <!-- Constrain to a comfortable reading measure (~70ch) so long
             soft-wrapped paragraphs don't stretch full-width. Article content
             only — the topic rail keeps its own column width. -->
        <div class="max-w-[46rem]">
          <h3 class="text-sm font-bold text-neutral-900 mb-3" data-testid="help-topic-title">{article.title}</h3>
          <div class="markdown-body text-[0.82rem] text-neutral-700 leading-relaxed">{@html renderMarkdown(article.body)}</div>
        </div>
      {:else}
        <p class="text-xs text-neutral-400">Выберите раздел слева.</p>
      {/if}
    </article>
  </div>
</div>
